import React, { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "../../config/config";
// import Navigation from "../navbar/Navbar";
import NavigationPaperDashboard from "../navbar/NavbarPaperDashboard";
import './inventory.css';
import './inventoryPage.css';

const InventoryPage = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [stockOutHistory, setStockOutHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchFilters, setSearchFilters] = useState({
    paperName: "",
    reelNumber: "",
    millName: "",
    shadeName: "",
    minDays: "",
    maxDays: ""
  });

  const naturalSortReel = (a, b) => {
    const extractParts = (reel) => {
      const match = reel.match(/([a-zA-Z-]+)(\d+)/);
      return match 
        ? { prefix: match[1], number: parseInt(match[2], 10) }
        : { prefix: reel, number: 0 };
    };
  
    const aParts = extractParts(a);
    const bParts = extractParts(b);
  
    if (aParts.prefix !== bParts.prefix) {
      return aParts.prefix.localeCompare(bParts.prefix);
    }
  
    return aParts.number - bParts.number;
  };



  // useEffect(() => {
  //   fetchInitialData();
  //   fetchStockOutHistory();
  // }, []);

  const processSortedData = (items) => {
    if (!sortColumn) return items;

    return [...items].sort((a, b) => {
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];

      if (['quantityLeft', 'days', 'ratePerKg', 'totalPrice'].includes(sortColumn)) {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }
      else if(sortColumn === 'reelNumber') {
        const sortMultiplier = sortDirection === 'asc' ? 1 : -1;
        return sortMultiplier * naturalSortReel(a[sortColumn], b[sortColumn]);
      }
      else if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter inventory by days (client-side filtering)
  const filterByDays = (items) => {
    if (!searchFilters.minDays && !searchFilters.maxDays) return items;
    
    return items.filter((item) => {
      const days = Number(item.days);
      const minDays = searchFilters.minDays ? Number(searchFilters.minDays) : null;
      const maxDays = searchFilters.maxDays ? Number(searchFilters.maxDays) : null;
      
      if (minDays !== null && maxDays !== null) {
        return days >= minDays && days <= maxDays;
      } else if (minDays !== null) {
        return days >= minDays;
      } else if (maxDays !== null) {
        return days <= maxDays;
      }
      return true;
    });
  };

  // First filter by days, then sort
  const filteredData = filterByDays(inventoryData);
  const processedData = processSortedData(filteredData);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = processedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  


  const calculateQuantityUsed = (reelNumber) => {
    return stockOutHistory
      .filter((history) => history.reel_number === reelNumber)
      .reduce(
        (total, history) => total + (Number(history.quantity_used) || 0),
        0
      );
  };

  const processInventoryData = (inventoryItems, history) => {
    return inventoryItems.map((item) => {
      const quantityUsed = history
        .filter((record) => record.reel_number === item.reelNumber)
        .reduce((total, record) => total + Number(record.quantity_used), 0);
      
      const quantityLeft = Number(item.quantity) - quantityUsed;
      const totalPrice = quantityLeft * Number(item.ratePerKg);
      const status = quantityUsed > 0 ? "Used" : "Fresh";

      return {
        ...item,
        quantityUsed,
        quantityLeft,
        totalPrice,
        status
      };
    }).filter(item => item.quantityLeft > 0);
  };

  const fetchData = async (filters = null) => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch both inventory and stock out history
      const [inventoryResponse, historyResponse] = await Promise.all([
        axios.get(API_ENDPOINTS.INVENTORY.SEARCH, {
          params: filters || {}
        }),
        axios.get(API_ENDPOINTS.REELS.HISTORY)
      ]);

      const inventoryData = Array.isArray(inventoryResponse.data)
        ? inventoryResponse.data
        : inventoryResponse.data.data || [];

      const historyData = Array.isArray(historyResponse.data) 
        ? historyResponse.data 
        : [];

      setStockOutHistory(historyData);
      
      // Process inventory data with latest stock out history
      const processedData = processInventoryData(inventoryData, historyData);
      setInventoryData(processedData);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (err) {
      // Only show error if it's a real error, not just empty results
      if (err.response && err.response.status !== 404) {
        setError("Failed to fetch data");
      }
      console.error("Error fetching data:", err);
      setInventoryData([]);
      setStockOutHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchData();
  }, []);

  // When filters change
  useEffect(() => {
    if (Object.values(searchFilters).some(value => value !== "")) {
      fetchData(searchFilters);
    } else {
      // If all filters are cleared, fetch all data
      fetchData();
    }
  }, [searchFilters]);

  const calculateTotals = () => {
    const uniqueReels = new Set();
    
    return filteredData.reduce(
      (acc, item) => {
        uniqueReels.add(item.reelNumber);
        return {
          totalQuantity: acc.totalQuantity + item.quantityLeft,
          totalValue: acc.totalValue + item.totalPrice,
          reelCount: uniqueReels.size
        };
      },
      { totalQuantity: 0, totalValue: 0, reelCount: 0 }
    );
  };


  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const { totalQuantity, totalValue, reelCount } = calculateTotals();
 
   // Handling sort
   const handleSort = (column) => {
     if (sortColumn === column) {
       // If already sorting this column, toggle direction
       setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
     } else {
       // If new column, start with ascending
       setSortColumn(column);
       setSortDirection('asc');
     }
   };
 
   // Sorting icon component
   const SortIcon = ({ column }) => {
     if (sortColumn !== column) {
       return (
         <span className="ml-2 text-gray-300">
           ↕️
         </span>
       );
     }
     return sortDirection === 'asc' ? '⬆️' : '⬇️';
   };
   const handleInputChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReset = () => {
    setSearchFilters({
      paperName: "",
      reelNumber: "",
      millName: "",
      shadeName: "",
      minDays: "",
      maxDays: ""
    });
    setSortColumn(null);
    setSortDirection('asc');
    fetchData(); // Fetch fresh data without filters
  };

  // CSV Export Function
  const exportToCSV = () => {
    // Use the filtered and sorted data (same as what's displayed)
    const dataToExport = processedData;
    
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Status',
      'Reel Number',
      'Paper Name (ReelSz/GSM/BF)',
      'Qty Left (kg)',
      'Mill Name',
      'Shade',
      'Days',
      'Rate/kg (₹)',
      'Value (₹)',
      'Remark'
    ];

    // Convert data to CSV rows
    const csvRows = dataToExport.map(item => [
      item.status || '',
      item.reelNumber || '',
      item.paperName || '',
      item.quantityLeft || 0,
      item.millName || '',
      item.shade || '',
      item.days || 0,
      item.ratePerKg ? item.ratePerKg.toFixed(2) : '0.00',
      item.totalPrice ? item.totalPrice.toFixed(2) : '0.00',
      item.remark || '-'
    ]);

    // Add summary row at the top (after headers)
    const { totalQuantity, totalValue, reelCount } = calculateTotals();
    const summaryRow = [
      'SUMMARY',
      `Reel Count: ${reelCount}`,
      `Total Quantity: ${totalQuantity.toFixed(2)} kg`,
      '',
      '',
      '',
      '',
      '',
      `Total Value: ₹${totalValue.toFixed(2)}`,
      ''
    ];

    // Add empty row for spacing
    const emptyRow = ['', '', '', '', '', '', '', '', '', ''];

    // Combine all rows
    const allRows = [headers, summaryRow, emptyRow, ...csvRows];

    // Convert to CSV string
    const csvContent = allRows
      .map(row => 
        row.map(cell => {
          // Escape double quotes and wrap in quotes if contains comma, newline, or quote
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
      .join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    // Generate filename with current date and time
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
    const filename = `inventory_${dateStr}_${timeStr}.csv`;
    
    // Create download link
    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  return (
    <div className="inventory-page">
      <NavigationPaperDashboard />
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl text-2xl-inventory font-bold mb-4">Inventory Management</h1>

        <div className="search-container">
              <div className="search-grid">
                {/* Paper Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Search by Paper Name"
                    value={searchFilters.paperName}
                    onChange={(e) =>
                      handleInputChange("paperName", e.target.value)
                    }
                  />
                </div>

                {/* Mill Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Search by Mill Name"
                    value={searchFilters.millName}
                    onChange={(e) =>
                      handleInputChange("millName", e.target.value)
                    }
                  />
                </div>

                {/* Shade Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Search by Shade Name"
                    value={searchFilters.shadeName}
                    onChange={(e) =>
                      handleInputChange("shadeName", e.target.value)
                    }
                  />
                </div>

                {/* Reel Number */}
                <div>
                  <input
                    type="text"
                    placeholder="Search by Reel Number"
                    value={searchFilters.reelNumber}
                    onChange={(e) =>
                      handleInputChange("reelNumber", e.target.value)
                    }
                  />
                </div>

                {/* Days Range */}
                <div className="date-container">
                  <input
                    type="number"
                    placeholder="Min Days"
                    value={searchFilters.minDays}
                    onChange={(e) =>
                      handleInputChange("minDays", e.target.value)
                    }
                    min="0"
                  />
                  <input
                    type="number"
                    placeholder="Max Days"
                    value={searchFilters.maxDays}
                    onChange={(e) =>
                      handleInputChange("maxDays", e.target.value)
                    }
                    min={searchFilters.minDays || "0"}
                  />
                </div>

                {/* Reset Button */}
                <div>
                  <button
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>

                {/* Export Button */}
                <div>
                  <button
                    onClick={exportToCSV}
                    style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                    }}
                    title="Export filtered data to CSV"
                  >
                    📥 Export CSV
                  </button>
                </div>
              </div>
            </div>
        <div
          className="summary-box"
          style={{
            background: "#f8f9fa",
            padding: "15px",
            marginBottom: "20px",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "40px",
            }}
          >
            <div>
              <strong>Reel Count:</strong>{" "}
              <span style={{ color: "#2c5282" }}>
                {reelCount}
              </span>
            </div>
            <div>
              <strong>Total Quantity:</strong>{" "}
              <span style={{ color: "#2c5282" }}>
                {totalQuantity.toFixed(2)} kg
              </span>
            </div>
            <div>
              <strong>Total Price:</strong>{" "}
              <span style={{ color: "#2c5282" }}>₹{totalValue.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {loading && <p className="text-center text-blue-500">Loading...</p>}
        {error && <p className="text-center text-red-500">{error}</p>}

        <div className="table-container-inventory">
          <div className="table-wrapper">
            <table className="w-full border-collapse">
          <thead>
              <tr>
              <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('status')}
                >
                  Status <SortIcon column="status" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('reelNumber')}
                >
                  Reel Number <SortIcon column="reelNumber" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('paperName')}
                >
                  Paper Name (ReelSz/GSM/BF) <SortIcon column="paperName" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('quantityLeft')}
                >
                  Qty Left(kg) <SortIcon column="quantityLeft" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('millName')}
                >
                  Mill Name <SortIcon column="millName" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('shade')}
                >
                  Shade <SortIcon column="shade" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('days')}
                >
                  Days <SortIcon column="days" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('ratePerKg')}
                >
                 Rate/kg(₹) <SortIcon column="ratePerKg" />
                </th>
                <th 
                  className="border p-2 cursor-pointer" 
                  onClick={() => handleSort('totalPrice')}
                >
                  Value(₹) <SortIcon column="totalPrice" />
                </th>
                <th className="border p-2">Remark</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item) => (
                <tr
                  key={item.id}
                  className={item.quantityLeft === 0 ? "bg-red-50" : ""}
                >
                  <td className="border p-2">
                    <span className={`status ${item.status.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="border p-2">{item.reelNumber}</td>
                  <td className="border p-2">{item.paperName}</td>
                  <td className="border p-2 font-medium text-blue-600">
                    {item.quantityLeft}
                  </td>
                  <td className="border p-2">{item.millName}</td>
                  <td className="border p-2">{item.shade}</td>
                  <td className="border p-2">{item.days}</td>
                  <td className="border p-2">{item.ratePerKg.toFixed(2)}</td>
                  <td className="border p-2 font-medium text-green-600">
                    {item.totalPrice.toFixed(2)}
                  </td>
                  <td className="border p-2">{item.remark || "-"}</td>
                </tr>
              ))}
              {(!Array.isArray(inventoryData) || inventoryData.length === 0) &&
                !loading && (
                  <tr>
                    <td colSpan="10" className="text-center p-4">
                      No inventory data found
                    </td>
                  </tr>
                )}
            </tbody>

          </table>
          </div>
          
          {/* Pagination Controls */}
          {inventoryData.length > 0 && (
            <div className="pagination-container">
              <button
                className="pagination-button"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                ‹
              </button>

              <div className="pagination-info">
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{totalPages}</strong>
              </div>

              <button
                className="pagination-button"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryPage;